import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { UserData } from './user.model';
import { AppInitData } from './app-init.model';
import { AccountData } from './account.model';
import { MatDialog, MatDialogRef } from '@angular/material';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MessageDialogComponent } from '../shared/message-dialog.component';
import { ConfirmDialogComponent } from './confirm-dialog.component';
import { ToastMessageComponent } from './toast-message.component';
import { PageData } from './page.model';

@Injectable()
export class SharedService {
	public appInit: AppInitData;
	public appUser: UserData;
	public accountIndex: number;
	public accountIndexSet = new Subject<number>();
	public pageIdSet = new Subject<number>();
	public authErrorSet = new Subject();
	public activationCompletedSet = new Subject();
	public resetPasswordSet = new Subject();
	public secret: string;
	public breadCrumbs: string = null;

	CRUD_ACTION_CREATE = 'C';
	CRUD_ACTION_READ = 'R';
	CRUD_ACTION_UPDATE = 'U';
	CRUD_ACTION_DELETE = 'D';

	get currentAccount(): AccountData {
		return this.appUser.accounts[this.accountIndex];
	}

	constructor(private dialog: MatDialog, private snackBar: MatSnackBar) { }

	getPassengerFleetId(account: AccountData | string): string {
		const custId =
			typeof account === 'string'
				? account.trim().toUpperCase()
				: (account.custId || '').trim().toUpperCase();
		if (typeof account !== 'string' && account.fleetId) {
			return account.fleetId.trim().toUpperCase();
		}
		if (typeof account === 'string' && this.appUser && this.appUser.accounts) {
			const matchedAccount = this.appUser.accounts.find(
				(item) =>
					(item.custId || '').trim().toUpperCase() === custId &&
					(item.fleetId || '').trim() !== ''
			);
			if (matchedAccount && matchedAccount.fleetId) {
				return matchedAccount.fleetId.trim().toUpperCase();
			}
		}
		return '';
	}

	findAccountIndexByChargeForPassengers(chargeNbr: string): number {
		if (!this.appUser || !this.appUser.accounts) {
			return -1;
		}
		const charge = (chargeNbr || '').trim().toUpperCase();
		if (!charge) {
			return -1;
		}
		return this.appUser.accounts.findIndex(
			(account) =>
				(account.custId || '').trim().toUpperCase() === charge &&
				account.onHold === ''
		);
	}

	setAccountIndex(index: number) {
		if (this.accountIndex === index) {
			return;
		}
		this.accountIndex = index;
		localStorage.setItem('accountIndex', String(index));
		this.accountIndexSet.next(index);
	}

	findAccountIndexByChargeAndFleet(chargeNbr: string, fleetId: string): number {
		if (!this.appUser || !this.appUser.accounts) {
			return -1;
		}
		const charge = (chargeNbr || '').trim().toUpperCase();
		const fleet = (fleetId || '').trim().toUpperCase();
		if (!charge || !fleet) {
			return -1;
		}
		return this.appUser.accounts.findIndex(
			(account) =>
				(account.custId || '').trim().toUpperCase() === charge &&
				(account.fleetId || '').trim().toUpperCase() === fleet
		);
	}

	setAccountByChargeAndFleet(chargeNbr: string, fleetId: string): boolean {
		const index = this.findAccountIndexByChargeAndFleet(chargeNbr, fleetId);
		if (index < 0) {
			return false;
		}
		this.setAccountIndex(index);
		return true;
	}

	setPageId(index: number) {
		this.pageIdSet.next(index);
	}

	setAuthError() {
		this.clearAuthSession();
		this.authErrorSet.next();
	}

	persistAuthSession() {
		if (!this.appUser || !this.appUser.identity || !this.appUser.identity.Token) {
			return;
		}
		localStorage.setItem('identityToken', this.appUser.identity.Token);
		if (this.appUser.sessionId) {
			localStorage.setItem('sessionId', this.appUser.sessionId);
		}
		const snapshot = Object.assign({}, this.appUser);
		delete snapshot.accounts;
		localStorage.setItem('sessionUser', JSON.stringify(snapshot));
	}

	loadAuthSession(): UserData {
		const token = localStorage.getItem('identityToken');
		const sessionId = localStorage.getItem('sessionId');
		const raw = localStorage.getItem('sessionUser');
		if (!token || !sessionId || !raw) {
			return undefined;
		}
		try {
			const user = JSON.parse(raw) as UserData;
			if (!user || !user.userId || !user.zoneId || user.webUserId == null) {
				return undefined;
			}
			user.identity = user.identity || { IdentityId: '', Token: '' };
			user.identity.Token = token;
			user.sessionId = sessionId;
			return user;
		} catch (err) {
			return undefined;
		}
	}

	clearAuthSession() {
		localStorage.removeItem('identityToken');
		localStorage.removeItem('sessionId');
		localStorage.removeItem('sessionUser');
		localStorage.removeItem('accountIndex');
	}

	setActivationCompleted() {
		this.activationCompletedSet.next();
	}
	resetPasswordCompleted() {
		this.resetPasswordSet.next();
	}

	getReservationPath() {
		let path = '';
		switch (this.appUser.zoneId) {
			case 'asc':
			case 'aus':
			case 'bna':
			case 'cle':
			case 'clt':
			case 'co':
			case 'cyc':
			case 'gai':
			case 'iad':
			case 'mco':
			case 'mem':
			case 'msp':
			case 'odo':
			case 'pbi':
			case 'rdu':
			case 'rsw':
			case 'san':
			case 'sdf':
			case 'ccsi':
				path = this.appInit.ccsiReservationPath;
				break;
			case 'b&l':
			case 'gos':
			case 'tlh':
			case 'tpa':
			case 'bnl':
				path = this.appInit.bnlReservationPath;
				break;
		}
		return path;
	}

	formatGetDateTime(date: Date) {
		// const d =
		//     date.getUTCFullYear() + '-' +
		//     (date.getUTCMonth() + 1).toString().padStart(2, '0') + '-' +
		//     date.getUTCDate().toString().padStart(2, '0') + 'T' +
		//     date.getUTCHours().toString().padStart(2, '0') + ':' +
		//     date.getUTCMinutes().toString().padStart(2, '0') + ':' +
		//     date.getUTCSeconds().toString().padStart(2, '0') + '.000Z';
		// return d;
		const year = date.getFullYear();
		const month = (date.getMonth() + 1).toString().padStart(2, '0');
		const day = date.getDate().toString().padStart(2, '0');
		const hours = date.getHours().toString().padStart(2, '0');
		const minutes = date.getMinutes().toString().padStart(2, '0');
		const seconds = date.getSeconds().toString().padStart(2, '0');

		return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
		// YYYY-MM-DD HH:MI:SS
	}

	displayDate(date: string): string {
		if (date === '') {
			return '';
		}
		const dt = new Date(date);
		const d =
			(dt.getUTCMonth() + 1).toString().padStart(2, '0') +
			'/' +
			dt.getUTCDate().toString().padStart(2, '0') +
			'/' +
			dt.getUTCFullYear();
		return d;
	}

	displayDateTime(date: string): string {
		if (date === '') {
			return '';
		}
		const dt = new Date(date);
		let d =
			(dt.getUTCMonth() + 1).toString().padStart(2, '0') +
			'/' +
			dt.getUTCDate().toString().padStart(2, '0') +
			'/' +
			dt.getUTCFullYear() +
			' ';
		if (this.isWillCall(dt)) {
			// handle will call trips
			d += '(will call)';
		} else {
			d +=
				this.getAmPmHour(dt).padStart(2, '0') +
				':' +
				dt.getUTCMinutes().toString().padStart(2, '0') +
				' ' +
				this.getAmPm(dt);
		}
		return d;
	}

	isWillCallFromIso(date: string) {
		const dt = new Date(date);
		return this.isWillCall(dt);
	}

	isWillCall(date: Date) {
		return date.getUTCHours() === 23 && date.getUTCMinutes() === 47;
	}

	nbrDisplay(nbr: number): string {
		if (nbr < 1) {
			return '';
		}
		return nbr.toString();
	}

	nameDisplay(lastName: string, firstName: string) {
		let name = '';
		if (lastName !== '') {
			name += lastName;
		}
		if (lastName !== '' && firstName !== '') {
			name += ', ';
		}
		if (firstName !== '') {
			name += firstName;
		}
		return name;
	}

	formatPhone(input: string): string {
		const output = this.unformatPhone(input).split('');
		if (output.length === 10) {
			output.splice(3, 0, '.');
			output.splice(7, 0, '.');
		}
		return output.join('');
	}

	unformatPhone(input: string): string {
		const chars = input.split('');
		let output = '';
		chars.forEach((char: string) => {
			if (output.length < 10) {
				if (!isNaN(parseInt(char, 10))) {
					output += char;
				}
			}
		});
		return output;
	}

	addressDisplay(strNbr: string, strName: string, city: string): string {
		let addr = '';
		if (strNbr !== '' && strName !== '') {
			addr += strNbr + ' ' + strName;
		}
		if (addr !== '' && city !== '') {
			addr += ', ';
		}
		if (city !== '') {
			addr += city;
		}
		return addr;
	}

	private getAmPmHour(dt: Date): string {
		const hour = dt.getUTCHours();
		if (hour === 0) {
			return '12';
		} else if (hour < 13) {
			return hour.toString();
		} else {
			return (hour - 12).toString();
		}
	}

	private getAmPm(dt: Date): string {
		if (dt.getUTCHours() < 12) {
			return 'AM';
		} else {
			return 'PM';
		}
	}

	openMessageDialog(
		title: string,
		message: string
	): MatDialogRef<MessageDialogComponent, any> {
		const dialogRef = this.dialog.open(MessageDialogComponent, {
			width: '400px',
			height: '250px',
			data: { title: title, message: message },
		});
		return dialogRef;
	}

	openConfirmDialog(
		title: string,
		message: string
	): MatDialogRef<ConfirmDialogComponent, any> {
		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			width: '400px',
			height: '250px',
			data: { title: title, message: message },
		});
		return dialogRef;
	}

	openToastMessage(
		message: string,
		isError: boolean = false,
		durationMs: number = 2000
	): void {
		this.snackBar.openFromComponent(ToastMessageComponent, {
			duration: durationMs,
			data: {
				message,
				isError,
			},
			horizontalPosition: 'right',
			verticalPosition: 'top',
			panelClass: isError ? ['app-toast-error'] : ['app-toast-success'],
		});
	}

	/** If name is all-uppercase, display in title case (including text in parentheses); otherwise return as-is. */
	titleCaseAccountName(name: string): string {
		if (!name) return name;
		// Only transform when name is all caps (match AccountNamePipe behavior)
		if (!/[A-Z]/.test(name) || /[a-z]/.test(name)) return name;
		const toTitleCase = (s: string) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
		const inParens: string[] = [];
		const placeholder = '§';
		let result = name.replace(/\(([^)]*)\)/g, (_, inner) => {
			const index = inParens.length;
			inParens.push('(' + toTitleCase(inner) + ')');
			return placeholder + index + placeholder;
		});
		result = toTitleCase(result);
		inParens.forEach((replacement, i) => {
			result = result.replace(placeholder + i + placeholder, replacement);
		});
		return result;
	}

	buildBreadCrumbs(path, appUser) {
		// build the breadcrumb trail based on what page the client
		// is currently on and the current account index
		this.breadCrumbs = '';
		const accountIndex = localStorage.getItem('accountIndex');
		switch (path) {
			case 'track-trips':
				this.breadCrumbs +=
					' <b>></b> Manage Trips <b>></b> ' +
					this.titleCaseAccountName(appUser.accounts[accountIndex].name);
				break;
			case 'user-profile':
				this.breadCrumbs += ' <b>></b> User Profile';
				break;
			case 'admin-tasks':
				this.breadCrumbs += ' <b>></b> Manage Users';
				break;
			case 'phone-booking':
				this.breadCrumbs +=
					' <b>></b> Phone Booking Authorization <b>></b> ' +
					this.titleCaseAccountName(appUser.accounts[accountIndex].name);
				break;
			case 'mobile-app-booking':
				this.breadCrumbs +=
					' <b>></b> Mobile App Booking Authorization <b>></b> ' +
					this.titleCaseAccountName(appUser.accounts[accountIndex].name);
				break;
			case 'passengers':
				this.breadCrumbs +=
					' <b>></b> Passengers <b>></b> ' +
					this.titleCaseAccountName(appUser.accounts[accountIndex].name);
				break;
			default:
				this.breadCrumbs = null;
		}
	}

	private shift: number = 3;

	// Encode: shifts letters/digits but preserves type
	encode(input: string): string {
		return input.split("").map(char => {
			if (/[A-Z]/.test(char)) {
				return String.fromCharCode(((char.charCodeAt(0) - 65 + this.shift) % 26) + 65); // uppercase
			} else if (/[a-z]/.test(char)) {
				return String.fromCharCode(((char.charCodeAt(0) - 97 + this.shift) % 26) + 97); // lowercase
			} else if (/[0-9]/.test(char)) {
				return String.fromCharCode(((char.charCodeAt(0) - 48 + this.shift) % 10) + 48); // digit
			} else {
				return char; // non-alphanumerics unchanged
			}
		}).join("");
	}

	// Decode: reverses the above logic
	decode(input: string): string {
		return input.split("").map(char => {
			if (/[A-Z]/.test(char)) {
				return String.fromCharCode(((char.charCodeAt(0) - 65 - this.shift + 26) % 26) + 65);
			} else if (/[a-z]/.test(char)) {
				return String.fromCharCode(((char.charCodeAt(0) - 97 - this.shift + 26) % 26) + 97);
			} else if (/[0-9]/.test(char)) {
				return String.fromCharCode(((char.charCodeAt(0) - 48 - this.shift + 10) % 10) + 48);
			} else {
				return char;
			}
		}).join("");
	}
}
